import { Device } from '@capacitor/device';

export const DEVICE_BATTERY_CACHE_MS = 5 * 60 * 1000;

export interface DeviceBatteryMetadata {
  /** Battery percentage, from 0 to 100. */
  readonly level: number;
  readonly isCharging?: boolean;
  /** Epoch milliseconds of the successful reading, including when served stale. */
  readonly lastChecked: number;
  readonly cache: 'yes';
}

let cachedBattery: DeviceBatteryMetadata | undefined;
let pendingRead: Promise<void> | undefined;
let nextRefreshAt = 0;

/**
 * Returns the last reading immediately. Requests trigger a single deferred read
 * when due; no caller waits for it, and no timer polls while requests are idle.
 */
export function getCachedDeviceBattery(): DeviceBatteryMetadata | undefined {
  const snapshot = cachedBattery;
  if (!pendingRead && Date.now() >= nextRefreshAt) {
    // Defer the plugin invocation too: synchronous plugin errors must not escape
    // into request construction, and the pending guard is set before it runs.
    pendingRead = Promise.resolve()
      .then(() => Device.getBatteryInfo())
      .then((info) => {
        const level = info?.batteryLevel;
        if (typeof level !== 'number' || !Number.isFinite(level) || level < 0 || level > 1) return;

        cachedBattery = {
          level: Math.round(level * 100),
          ...(typeof info.isCharging === 'boolean' ? { isCharging: info.isCharging } : {}),
          lastChecked: Date.now(),
          cache: 'yes',
        };
      })
      .catch(() => {
        // Optional telemetry: unsupported platforms and failures keep old data.
      })
      .finally(() => {
        // Failed/invalid reads also back off, without changing the old timestamp.
        nextRefreshAt = Date.now() + DEVICE_BATTERY_CACHE_MS;
        pendingRead = undefined;
      });
  }
  return snapshot;
}
